// Static configuration for the Chart of Accounts feature.
// Palette + templates + detail-type dropdowns + default type totals + default Vela suggestions.
// Table column defs + shared column widths used by both the Dashboard sticky header
// and the AccountsTable body (both must use the same widths so columns align).

import type { AccountType, Template, TypeTotals, VelaSuggestion } from "./types";
import type { TableColumnDef } from "../../../components/common/tableCustomization";

export const PALETTE = {
  ink: "#0E1A1A",
  ink2: "#263238",
  ink3: "#3A4A52",
  ink4: "#6B7684",
  line: "#D8DEE6",
  lineSoft: "#E7EAF0",
  panel: "#F7F8FA",
  teal: "#15A08C",
  tealDark: "#0F6E56",
  tealWash: "#E1F5EE",
  amberWash: "#FEF3C7",
  amberInk: "#92400E",
  red: "#DC2626",
  redInk: "#991B1B",
  redWash: "#FEE2E2",
  velaDeep: "#4C1D95",
  vela: "#6B46C1",
  velaWash: "#EDE9FE",
  velaFade: "#F5F3FF",
} as const;

export const STAT_COLORS: Record<AccountType, string> = {
  Asset: PALETTE.tealDark,
  Liability: PALETTE.redInk,
  Equity: PALETTE.amberInk,
  Income: PALETTE.tealDark,
  Expense: PALETTE.velaDeep,
};

export const DETAIL_TYPES_BY_TYPE: Record<AccountType, string[]> = {
  Asset: ["Chequing", "Savings", "Accounts receivable", "Supplies on hand", "Prepaid expense", "Equipment", "Vehicle", "Other asset"],
  Liability: ["Credit card", "Accounts payable", "Payroll liabilities", "Sales tax payable", "Loans payable", "Other liability"],
  Equity: ["Owner's equity", "Owner's draw", "Retained earnings", "Shareholder loan"],
  Income: ["Service revenue", "Product sales", "Government billing", "Insurance billing", "Interest income", "Other income"],
  Expense: ["Wages and salaries", "Office supplies", "Vehicle and fuel", "Professional services", "Telecommunications", "Software and subscriptions", "Travel", "Medical supplies", "Office rent", "Insurance", "Other expense"],
};

export const TYPE_NUMBER_START: Record<AccountType, number> = {
  Asset: 1000,
  Liability: 2000,
  Equity: 3000,
  Income: 4000,
  Expense: 6000,
};

export const TEMPLATES: Template[] = [
  { id: "general", icon: "Building2", name: "General business", description: "Services, consulting, software, small retail. A safe starting point for most businesses.", accountCount: 34 },
  { id: "healthcare", icon: "Stethoscope", name: "Healthcare and home services", description: "Home care, clinics, caregivers. Client billing, staff wages, supplies, insurance billing.", accountCount: 42, recommended: true },
  { id: "retail", icon: "Package", name: "Retail and e-commerce", description: "Online store or storefront. Inventory, COGS, shipping, merchant fees, sales tax.", accountCount: 47 },
];

export const DEFAULT_TYPE_TOTALS: TypeTotals = {
  Asset: { total: 75230, count: 8, change: "up 4%" },
  Liability: { total: 8565, count: 7, change: "up 2%" },
  Equity: { total: 66664, count: 3 },
  Income: { total: 98420, count: 6, change: "up 12%" },
  Expense: { total: 41208, count: 18, change: "down 3%" },
};

export const DEFAULT_VELA_SUGGESTIONS: VelaSuggestion[] = [
  { id: "s1", type: "add", typeLabel: "Add account", title: 'Add "Vehicle expenses" account', description: "15 transactions in the last 90 days look like fuel-station charges. No account for them yet.", primaryAction: "Add account" },
  { id: "s2", type: "archive", typeLabel: "Archive unused", title: "4 accounts quiet 90+ days", description: "Prepaid rent, Shareholder loans, Trade shows, Legal fees. Archive to keep the chart clean; re-activate any time.", primaryAction: "Archive 4" },
  { id: "s3", type: "merge", typeLabel: "Merge duplicate", title: '"Software" and "Software subscriptions" overlap', description: "Same vendors show up in both. Merge into one account?", primaryAction: "Merge" },
];

// Column defs for the Chart of accounts table. Fed to useTableCustomization + TableCustomizeDrawer.
// Name is always on; the rest are togglable.
export const COA_TABLE_COLUMNS: TableColumnDef[] = [
  { key: "name", label: "Name", togglable: false, defaultVisible: true },
  { key: "type", label: "Account type", togglable: true, defaultVisible: true },
  { key: "detailType", label: "Detail type", togglable: true, defaultVisible: true },
  { key: "novalaBalance", label: "Novala balance", togglable: true, defaultVisible: true },
  { key: "bankBalance", label: "Bank balance", togglable: true, defaultVisible: true },
];

// Shared column widths used by both the sticky table header (in Dashboard)
// and the scrolling table body (in AccountsTable). Must match exactly so
// the header columns align with the body cells. `null` means flex (shares
// remaining space); all other columns use fixed pixel widths.
export const COA_COL_WIDTHS: Record<string, number | null> = {
  checkbox: 40,
  name: null,
  type: 180,
  detailType: 220,
  novalaBalance: 160,
  bankBalance: 160,
  action: 140,
};